import { z } from 'zod';

/**
 * The GPT-Live server events a voice call acts on. Anything else (usage
 * updates, append acknowledgements) is ignored.
 */
const LiveServerEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('session.started') }),
  // Audio frames carry no timestamps (measured against the live API): the
  // stream is one continuous 100 ms-per-frame track, silence included.
  z.object({
    type: z.literal('session.output_audio.delta'),
    delta: z.string(),
  }),
  z.object({
    type: z.literal('session.input_transcript.delta'),
    delta: z.string(),
    start_ms: z.number(),
    end_ms: z.number(),
  }),
  z.object({
    type: z.literal('session.output_transcript.delta'),
    delta: z.string(),
    start_ms: z.number(),
    // Documented, but not relied on: older sessions sent only `start_ms`.
    end_ms: z.number().optional(),
  }),
  z.object({
    type: z.literal('session.delegation.created'),
    delegation: z.object({ id: z.string(), target: z.string() }),
  }),
  z.object({ type: z.literal('session.closed'), reason: z.string() }),
  z.object({
    type: z.literal('error'),
    error: z.object({ message: z.string() }),
  }),
]);

export type LiveServerEvent = z.infer<typeof LiveServerEventSchema>;

export function parseLiveServerEvent(raw: string): LiveServerEvent | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  // Ten audio frames a second: skip schema validation of ~5 KB strings.
  if (
    typeof json === 'object' &&
    json !== null &&
    'type' in json &&
    json.type === 'session.output_audio.delta' &&
    'delta' in json &&
    typeof json.delta === 'string'
  ) {
    return { type: 'session.output_audio.delta', delta: json.delta };
  }
  const result = LiveServerEventSchema.safeParse(json);
  return result.success ? result.data : null;
}

export interface LiveEventHandlers {
  started: () => void;
  audio: (base64: string) => void;
  userSpeech: (fragment: {
    delta: string;
    start_ms: number;
    end_ms: number;
  }) => void;
  assistantSpeech: (delta: string, startMs: number, endMs?: number) => void;
  /** A delegation GPT-Live wants this client to answer. */
  delegation: (id: string) => void;
  closed: () => void;
}

/** Parse one server frame and route it to its handler. */
export function dispatchLiveEvent(raw: string, on: LiveEventHandlers): void {
  const event = parseLiveServerEvent(raw);
  if (!event) return;
  switch (event.type) {
    case 'session.started':
      return on.started();
    case 'session.output_audio.delta':
      return on.audio(event.delta);
    case 'session.input_transcript.delta':
      return on.userSpeech(event);
    case 'session.output_transcript.delta':
      return on.assistantSpeech(event.delta, event.start_ms, event.end_ms);
    case 'session.delegation.created':
      if (event.delegation.target === 'client')
        on.delegation(event.delegation.id);
      return;
    case 'session.closed':
      return on.closed();
    case 'error':
      console.error('[voice-call] GPT-Live error:', event.error.message);
  }
}

/** `/api` or `https://api.host/api` + `/api/x` → `wss://api.host/api/x?ticket=…` */
export function buildVoiceCallUrl(
  apiBaseUrl: string,
  wsPath: string,
  ticket: string,
  pageOrigin: string,
): string {
  const apiOrigin = new URL(apiBaseUrl, pageOrigin).origin;
  const url = new URL(wsPath, apiOrigin);
  url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
  url.searchParams.set('ticket', ticket);
  return url.toString();
}
